import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import type { AdminUser, AdminUsersPage } from '@/domains/types'

export const adminUserKeys = {
  all: ['admin-users'] as const,
  list: (filter: string, page: number) => ['admin-users', 'list', filter, page] as const,
}

export function useGetAdminUsers(
  filter: 'all' | 'pending' | 'enabled' | 'disabled' = 'all',
  page = 1,
  limit = 20,
) {
  return useQuery({
    queryKey: adminUserKeys.list(filter, page),
    queryFn: async (): Promise<AdminUsersPage> => {
      const response = await apiClient.get<AdminUsersPage>('/users/admin', {
        params: { filter, page, limit },
      })
      return response.data
    },
  })
}

export function useEnableUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string): Promise<AdminUser> => {
      const response = await apiClient.patch<AdminUser>(`/users/admin/${userId}/enable`)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDisableUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string): Promise<AdminUser> => {
      const response = await apiClient.patch<AdminUser>(`/users/admin/${userId}/disable`)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id: userId, role }: { id: string; role: string }): Promise<AdminUser> => {
      const response = await apiClient.patch<AdminUser>(`/users/admin/${userId}/role`, { role })
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteAdminUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: string): Promise<{ success: boolean }> => {
      const response = await apiClient.delete<{ success: boolean }>(`/users/admin/${userId}`)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminUserKeys.all })
    },
    onError: (error) => {
      throw new Error(handleApiError(error))
    },
  })
}
